import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { StatusPill, Thread } from "@/components/site/TicketParts";

const STATUSES = ["open", "in_progress", "resolved", "closed"];
const label = (s) => s.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase());
const trig = "h-10 rounded-xl border-slate-300 bg-white text-slate-900";

const Stats = ({ stats }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
    {["total", ...STATUSES].map((k) => (
      <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`stat-${k}`}>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label(k)}</p>
        <p className="font-display mt-1 text-2xl font-bold text-slate-900">{stats?.[k] ?? 0}</p>
      </div>
    ))}
  </div>
);

const Detail = ({ t, onChange }) => {
  const [msg, setMsg] = useState("");
  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); onChange(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const reply = (e) => { e.preventDefault(); run(async () => { await api.post(`/admin/tickets/${t.code}/reply`, { message: msg }); setMsg(""); }, "Reply sent"); };
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="admin-ticket-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><p className="font-mono-x text-xs font-bold text-blue-600">{t.code} · {t.priority} priority</p><h3 className="font-display text-lg font-bold text-slate-900">{t.subject}</h3><p className="text-sm text-slate-600">{t.name} · <a href={`mailto:${t.email}`} className="text-blue-600">{t.email}</a></p></div>
        <div className="flex items-center gap-2">
          <Select value={t.status} onValueChange={(v) => run(() => api.patch(`/admin/tickets/${t.code}`, { status: v }), "Status updated")}>
            <SelectTrigger className={`w-40 ${trig}`} data-testid="admin-ticket-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`admin-status-option-${s}`}>{label(s)}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={() => window.confirm("Delete this ticket?") && run(() => api.delete(`/admin/tickets/${t.code}`), "Ticket deleted")} data-testid="admin-ticket-delete-button" className="rounded-xl border border-slate-300 p-2.5 text-slate-600 hover:border-red-400 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-6"><Thread ticket={t} viewer="support" /></div>
      <form onSubmit={reply} className="mt-6 space-y-3">
        <Textarea required rows={4} placeholder="Write a reply to the customer…" value={msg} onChange={(e) => setMsg(e.target.value)} className="rounded-xl border-slate-300 bg-white text-slate-900 placeholder:text-slate-400" data-testid="admin-reply-input" />
        <button data-testid="admin-reply-button" className="rounded-full bg-blue-600 px-6 py-2.5 text-sm font-bold text-white">Send reply</button>
      </form>
    </div>
  );
};

export const TicketManager = () => {
  const [filter, setFilter] = useState("all");
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([api.get("/admin/tickets", { params: { status: filter } }), api.get("/admin/stats")]);
      setTickets(Array.isArray(t.data) ? t.data : []);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const current = tickets.find((t) => t.code === sel);
  return (
    <div className="space-y-6">
      <Stats stats={stats} />
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className={trig} data-testid="admin-ticket-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{["all", ...STATUSES].map((s) => <SelectItem key={s} value={s} data-testid={`admin-filter-${s}`}>{s === "all" ? "All tickets" : label(s)}</SelectItem>)}</SelectContent>
          </Select>
          <div className="mt-4 max-h-[560px] space-y-2 overflow-y-auto" data-testid="admin-ticket-list">
            {tickets.length === 0 && <p className="flex items-center gap-2 p-4 text-sm text-slate-600"><Inbox className="h-4 w-4" /> No tickets yet.</p>}
            {tickets.map((t) => (
              <button key={t.code} onClick={() => setSel(t.code)} data-testid={`admin-ticket-${t.code}`} className={`w-full rounded-2xl border p-4 text-left transition-colors ${sel === t.code ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex items-center justify-between gap-2"><span className="font-mono-x text-xs font-bold text-blue-600">{t.code}</span><StatusPill status={t.status} /></div>
                <p className="mt-1.5 truncate font-semibold text-slate-900">{t.subject}</p>
                <p className="truncate text-xs text-slate-600">{t.name} · {new Date(t.updated_at).toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>
        {current ? <Detail key={current.code} t={current} onChange={load} /> : <div className="flex min-h-[300px] items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white text-sm text-slate-600" data-testid="admin-ticket-empty">Select a ticket to view and reply.</div>}
      </div>
    </div>
  );
};
